import {config} from "@/constants/config";
import type {TResponseRequest} from "@/interfaces/http.interface";
import httpRequest from "@/providers/http/request.http";
import {buildQueryUrl} from "@/providers/http/request.instance";
import {type PaginatedResult, normalizePagination} from "@/utils/pagination";

export interface Country {
  id: string;
  name: string;
  currency: string;
  currencyCode: string;
  phoneCode: string;
  rate: number;
  isAvailable: boolean;
  createdAt: string;
  updatedAt: string;
}

interface CountryFilters {
  page?: number;
  limit?: number;
  isAvailable?: boolean;
}

export type CountryListResult = PaginatedResult<Country>;

export const getCountriesService = async (
  filters: CountryFilters = {}
): Promise<TResponseRequest<CountryListResult>> => {
  const raw = await httpRequest.get<Country[]>({
    url: buildQueryUrl(`${config.apiBaseUrl}/bo/countries`, filters as Record<string, unknown>),
  });
  if (!raw.data || !raw.pagination) return {...raw, data: undefined};
  return {...raw, data: {items: raw.data, pagination: normalizePagination(raw.pagination)}};
};

export const getCountryByIdService = async (id: string): Promise<TResponseRequest<Country>> => {
  return httpRequest.get<Country>({
    url: `${config.apiBaseUrl}/bo/countries/${encodeURIComponent(id)}`,
  });
};

export const createCountryService = async (body: {
  name: string;
  currency: string;
  currencyCode: string;
  phoneCode: string;
  rate: number;
}): Promise<TResponseRequest<Country>> => {
  return httpRequest.post<Country>({
    url: `${config.apiBaseUrl}/bo/countries`,
    body,
  });
};

export const updateCountryService = async (
  id: string,
  body: Partial<{
    name: string;
    currency: string;
    currencyCode: string;
    phoneCode: string;
    rate: number;
    isAvailable: boolean;
  }>
): Promise<TResponseRequest<Country>> => {
  return httpRequest.patch<Country>({
    url: `${config.apiBaseUrl}/bo/countries/${encodeURIComponent(id)}`,
    body,
  });
};

export const deleteCountryService = async (id: string): Promise<TResponseRequest<void>> => {
  return httpRequest.delete<void>({
    url: `${config.apiBaseUrl}/bo/countries/${encodeURIComponent(id)}`,
    body: {},
  });
};
